import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import apiService, {
  LOUPE_CUE_CLASSES,
  type ReviewAuditDatasetFrame,
  type ReviewAuditCaseClip,
  type ReviewAuditCaseDetail,
  type ReviewAuditCaseKind,
  type ReviewAuditCaseLastProbe,
  type ReviewAuditCaseLoupeFrame,
  type ReviewAuditCaseMention,
  type ReviewAuditCaseProbe,
  type ReviewAuditCasesSummary,
} from "../services/api";
import { ROUTES_NAMES } from "../components/routes";
import {
  youtubeEmbedUrlFromWatchUrl,
  youtubeWatchUrlFromWatchUrl,
} from "../utils/youtubeVideoId";
import {
  MIN_TOKENS as FINGERPRINT_MIN_WORDS,
  fingerprintTaggedLines,
} from "../utils/transcriptFingerprintMatch";

const DEFAULT_LOOKBACK_DAYS = 14;

const KIND_LABEL: Record<ReviewAuditCaseKind, string> = {
  rejected: "Rejected",
  edited: "Edited",
  added: "Missed (added manually)",
};

const KIND_COLOR: Record<ReviewAuditCaseKind, "error" | "warning" | "info"> = {
  rejected: "error",
  edited: "warning",
  added: "info",
};
const CLIP_LEAD_SEC = 2;
const CLIP_DOWNLOAD_PAD_SEC = 10;
const COLUMN_MAX_HEIGHT = 520;

function ymdDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function clock(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return "—";
  const s = Math.max(0, Math.floor(sec));
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
}

function CopyId({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {label}{" "}
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          {value}
        </Box>
      </Typography>
      <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
        <IconButton
          size="small"
          aria-label={`Copy ${label}`}
          onClick={() => {
            void navigator.clipboard
              .writeText(value)
              .then(() => setCopied(true))
              .catch(() => undefined);
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 14 }} color="success" />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function DiffRow({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  const changed = before !== after;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "140px 1fr 1fr",
        gap: 1,
        py: 0.25,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={
          changed
            ? { textDecoration: "line-through", color: "error.main" }
            : undefined
        }
      >
        {before}
      </Typography>
      <Typography
        variant="body2"
        sx={changed ? { color: "success.main", fontWeight: 600 } : undefined}
      >
        {after}
      </Typography>
    </Box>
  );
}

function CaseClassification({ detail }: { detail: ReviewAuditCaseDetail }) {
  const m = detail.mention;
  const o = m.original;
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={KIND_COLOR[m.kind]}
          label={KIND_LABEL[m.kind]}
        />
        {m.kind !== "added" &&
          detail.categories.map((c) => (
            <Chip
              key={c.mode}
              size="small"
              variant="outlined"
              label={c.label}
            />
          ))}
        {m.evidenceType && (
          <Chip
            size="small"
            variant="outlined"
            label={`evidence: ${m.evidenceType}`}
          />
        )}
        {m.visualCue && (
          <Chip size="small" variant="outlined" label={`cue: ${m.visualCue}`} />
        )}
        {m.model && <Chip size="small" variant="outlined" label={m.model} />}
      </Stack>

      {m.kind === "added" && (
        <Typography variant="body2">
          The AI did not propose this mention. A reviewer added it:{" "}
          <strong>{m.brandName}</strong> · {m.mentionType ?? "—"} ·{" "}
          {m.placementType ?? "—"}
        </Typography>
      )}

      {m.kind === "rejected" && (
        <Typography variant="body2">
          <strong>Reject reason:</strong> {m.rejectReason || "(no reason)"}
        </Typography>
      )}

      {m.kind === "edited" && o && (
        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "140px 1fr 1fr",
              gap: 1,
            }}
          >
            <span />
            <Typography variant="caption" color="text.secondary">
              AI
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Human
            </Typography>
          </Box>
          <DiffRow
            label="Brand"
            before={o.brandName ?? m.brandName}
            after={m.brandName}
          />
          <DiffRow
            label="Time"
            before={`${clock(o.segmentStart ?? m.segmentStart)}–${clock(o.segmentEnd ?? m.segmentEnd)}`}
            after={`${clock(m.segmentStart)}–${clock(m.segmentEnd)}`}
          />
          <DiffRow
            label="Mention type"
            before={o.mentionType ?? m.mentionType ?? "—"}
            after={m.mentionType ?? "—"}
          />
          <DiffRow
            label="Placement"
            before={o.placementType ?? m.placementType ?? "—"}
            after={m.placementType ?? "—"}
          />
          <DiffRow
            label="Visual cue"
            before={
              (o.visualCue !== undefined ? o.visualCue : m.visualCue) ?? "—"
            }
            after={m.visualCue ?? "—"}
          />
        </Box>
      )}

      {m.mentionText && (
        <Typography variant="body2" color="text.secondary">
          <strong>Mention text:</strong> {m.mentionText}
        </Typography>
      )}
    </Stack>
  );
}

function brandFingerprintsKey(brandId: string | null) {
  return ["brandFingerprints", brandId] as const;
}

function useBrandFingerprints(brandId: string | null) {
  return useQuery({
    queryKey: brandFingerprintsKey(brandId),
    queryFn: () => apiService.getBrandTranscriptFingerprints(brandId as string),
    enabled: Boolean(brandId),
    staleTime: 60_000,
  });
}

function BrandFingerprints({ brandId }: { brandId: string }) {
  const queryClient = useQueryClient();
  const { data: fingerprints = [] } = useBrandFingerprints(brandId);
  const [editing, setEditing] = useState<{ mentionId: string; text: string } | null>(null);
  useEffect(() => setEditing(null), [brandId]);
  const remove = useMutation({
    mutationFn: (mentionId: string) =>
      apiService.removeBrandTranscriptFingerprint(brandId, mentionId),
    onSuccess: (next) => queryClient.setQueryData(brandFingerprintsKey(brandId), next),
  });
  const update = useMutation({
    mutationFn: (edit: { mentionId: string; text: string }) =>
      apiService.updateBrandTranscriptFingerprint(brandId, edit.mentionId, edit.text),
    onSuccess: (next) => {
      queryClient.setQueryData(brandFingerprintsKey(brandId), next);
      setEditing(null);
    },
  });

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        Saved sponsor reads ({fingerprints.length}) — transcript lines that repeat one are
        tagged as this brand for analyze
      </Typography>
      {fingerprints.map((fp) =>
        editing?.mentionId === fp.mentionId ? (
          <Stack key={fp.mentionId} spacing={1}>
            <TextField
              size="small"
              multiline
              minRows={2}
              fullWidth
              value={editing.text}
              onChange={(e) => setEditing({ mentionId: fp.mentionId, text: e.target.value })}
              helperText={`Keep the words that repeat every time the ad plays (${FINGERPRINT_MIN_WORDS}+ words). Trim intros or other ads that got joined in.`}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={update.isPending || !editing.text.trim() || editing.text === fp.text}
                onClick={() => update.mutate(editing)}
                startIcon={update.isPending ? <CircularProgress size={14} /> : undefined}
              >
                Save
              </Button>
              <Button
                size="small"
                disabled={update.isPending}
                onClick={() => {
                  setEditing(null);
                  update.reset();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack key={fp.mentionId} direction="row" spacing={1} alignItems="flex-start">
            <Typography
              variant="body2"
              sx={{ flex: 1, minWidth: 0, fontStyle: "italic" }}
              title={`from mention ${fp.mentionId}`}
            >
              “{fp.text}”
            </Typography>
            <Button
              size="small"
              disabled={remove.isPending || Boolean(editing)}
              onClick={() => {
                update.reset();
                setEditing({ mentionId: fp.mentionId, text: fp.text });
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              color="error"
              disabled={remove.isPending || Boolean(editing)}
              onClick={() => remove.mutate(fp.mentionId)}
            >
              Remove
            </Button>
          </Stack>
        ),
      )}
      {update.error && (
        <Alert severity="error">
          Failed to save the read. {(update.error as Error).message}
        </Alert>
      )}
      {remove.error && (
        <Alert severity="error">
          Failed to remove saved read. {(remove.error as Error).message}
        </Alert>
      )}
    </Stack>
  );
}

function BrandPanel({
  brandId,
  brandName,
}: {
  brandId: string | null;
  brandName: string;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["brand", brandId],
    queryFn: () => apiService.getBrand(brandId as string),
    enabled: Boolean(brandId),
    staleTime: 60_000,
  });
  const brand = data?.brand;

  const save = useMutation({
    mutationFn: (aliases: string[]) =>
      apiService.updateBrandAliases(brandId as string, aliases),
    onSuccess: (res) => {
      queryClient.setQueryData(["brand", brandId], (prev: typeof data) =>
        prev?.brand
          ? { ...prev, brand: { ...prev.brand, aliases: res.brand.aliases } }
          : prev,
      );
    },
  });

  useEffect(() => setDraft(""), [brandId]);

  if (!brandId) {
    return (
      <Alert severity="warning" variant="outlined">
        No brand record matches “{brandName}”, so there are no aliases to edit.
      </Alert>
    );
  }
  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <CircularProgress size={18} />
      </Paper>
    );
  }
  if (error || !brand) {
    return (
      <Alert severity="error">
        Failed to load brand. {(error as Error | null)?.message}
      </Alert>
    );
  }

  const aliases = brand.aliases ?? [];
  const addAlias = () => {
    const value = draft.trim();
    if (!value) return;
    if (aliases.some((a) => a.toLowerCase() === value.toLowerCase())) {
      setDraft("");
      return;
    }
    save.mutate([...aliases, value], { onSuccess: () => setDraft("") });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2">Brand</Typography>
          <Typography variant="body2">
            <strong>{brand.name}</strong>
          </Typography>
          {!brand.isActive && <Chip size="small" color="error" label="inactive" />}
          {!brand.canSponsor && (
            <Chip size="small" color="error" label="cannot sponsor" />
          )}
          {brand.category && (
            <Chip size="small" variant="outlined" label={brand.category} />
          )}
          <CopyId label="brand id" value={brand._id} />
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Aliases
          </Typography>
          {aliases.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              none
            </Typography>
          )}
          {aliases.map((alias) => (
            <Chip
              key={alias}
              size="small"
              label={alias}
              disabled={save.isPending}
              onDelete={() => save.mutate(aliases.filter((a) => a !== alias))}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Add alias (e.g. how the transcript spells it)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addAlias();
              }
            }}
            sx={{ flex: 1, maxWidth: 420 }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={!draft.trim() || save.isPending}
            onClick={addAlias}
          >
            Add
          </Button>
          {save.isPending && <CircularProgress size={16} />}
        </Stack>
        {save.error && (
          <Alert severity="error">
            Failed to save aliases. {(save.error as Error).message}
          </Alert>
        )}
        <BrandFingerprints brandId={brandId} />
        <Typography variant="caption" color="text.secondary">
          Alias and saved-read changes apply to the next Replay analyze.
        </Typography>
      </Stack>
    </Paper>
  );
}

function CasePanel({
  mentionId,
  onReplayed,
}: {
  mentionId: string;
  onReplayed?: (mentionId: string) => void;
}) {
  const [seek, setSeek] = useState<{ sec: number; at: number } | null>(null);
  const seekTo = useCallback((sec: number) => setSeek({ sec, at: Date.now() }), []);
  const seekSec = seek?.sec ?? null;
  const [playheadSec, setPlayheadSec] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery<ReviewAuditCaseDetail>({
    queryKey: ["reviewAuditCase", mentionId],
    queryFn: () => apiService.getReviewAuditCase(mentionId),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    setSeek(null);
    setPlayheadSec(null);
  }, [mentionId]);

  const queryClient = useQueryClient();
  const replay = useMutation({
    mutationFn: () => apiService.runReviewAuditCaseProbe(mentionId),
    onSuccess: (probe) => {
      queryClient.setQueryData<ReviewAuditCaseDetail>(
        ["reviewAuditCase", mentionId],
        (prev) => (prev ? { ...prev, probe } : prev),
      );
      onReplayed?.(mentionId);
      void queryClient.invalidateQueries({ queryKey: ["reviewAuditCases"] });
    },
  });

  const { data: savedClip } = useQuery<ReviewAuditCaseClip | null>({
    queryKey: ["reviewAuditCaseClip", mentionId],
    queryFn: () => apiService.getReviewAuditCaseClip(mentionId),
  });
  const [localClip, setLocalClip] = useState<{ url: string; fileName: string } | null>(
    null,
  );
  const [clipHidden, setClipHidden] = useState(false);
  useEffect(() => {
    setLocalClip(null);
    setClipHidden(false);
    if (!savedClip) return undefined;
    let url: string | null = null;
    let cancelled = false;
    void apiService.getReviewAuditCaseClipFile(mentionId).then((blob) => {
      if (cancelled) return;
      url = URL.createObjectURL(blob);
      setLocalClip({ url, fileName: savedClip.fileName });
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [mentionId, savedClip]);

  const caseBrandId = data?.mention.brandId ?? null;
  const { data: brandFingerprints = [] } = useBrandFingerprints(caseBrandId);
  const wordingSaved = brandFingerprints.some((fp) => fp.mentionId === mentionId);
  const taggedLines = useMemo(
    () =>
      fingerprintTaggedLines(
        data?.transcript ?? [],
        brandFingerprints.map((fp) => fp.text),
        data ? [data.mention.brandName] : [],
      ),
    [data, brandFingerprints],
  );
  const rememberWording = useMutation({
    mutationFn: () => apiService.rememberReviewAuditCaseWording(mentionId),
    onSuccess: (res) =>
      queryClient.setQueryData(brandFingerprintsKey(res.brandId), res.fingerprints),
  });

  const clip = useMutation({
    mutationFn: () => apiService.extractReviewAuditCaseClip(mentionId, CLIP_DOWNLOAD_PAD_SEC),
    onSuccess: (record) => {
      queryClient.setQueryData(["reviewAuditCaseClip", mentionId], record);
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error || !data) {
    return (
      <Alert severity="error">
        Failed to load case. {(error as Error | null)?.message}
      </Alert>
    );
  }

  const m = data.mention;
  const clipStart = seekSec ?? Math.max(0, m.segmentStart - CLIP_LEAD_SEC);
  const baseEmbed = youtubeEmbedUrlFromWatchUrl(data.contentUrl, clipStart);
  const embedUrl =
    baseEmbed && seekSec == null
      ? `${baseEmbed}&end=${Math.ceil(m.segmentEnd + CLIP_LEAD_SEC)}`
      : baseEmbed;
  const watchUrl = youtubeWatchUrlFromWatchUrl(data.contentUrl, clipStart);
  const useLocalClip = Boolean(savedClip && localClip && !clipHidden);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6">
            {m.brandName} · {clock(m.segmentStart)}–{clock(m.segmentEnd)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {m.podcastTitle} — {m.contentTitle}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <CopyId label="mention id" value={mentionId} />
            {data.probe?.probeId && (
              <CopyId label="replay id" value={data.probe.probeId} />
            )}
          </Stack>
        </Box>
        <Button
          variant="outlined"
          disabled={!m.brandId || wordingSaved || rememberWording.isPending}
          onClick={() => rememberWording.mutate()}
          title="Save this mention's transcript on the brand; analyze tags lines that repeat it"
          startIcon={
            rememberWording.isPending ? <CircularProgress size={16} /> : undefined
          }
        >
          {wordingSaved ? "Wording saved" : "Remember this wording"}
        </Button>
        <Button
          variant="outlined"
          disabled={clip.isPending || !data.contentUrl}
          onClick={() => {
            if (savedClip) setClipHidden(false);
            else clip.mutate();
          }}
          startIcon={clip.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {clip.isPending
            ? "Extracting clip…"
            : savedClip
              ? "Show clip"
              : `Extract clip ±${CLIP_DOWNLOAD_PAD_SEC}s`}
        </Button>
        <Button
          variant="outlined"
          disabled={replay.isPending}
          onClick={() => replay.mutate()}
          startIcon={
            replay.isPending ? <CircularProgress size={16} /> : undefined
          }
        >
          {replay.isPending ? "Replaying analyze…" : "Replay analyze"}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <CaseClassification detail={data} />
      </Paper>

      <BrandPanel brandId={m.brandId} brandName={m.brandName} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.7fr)",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, pb: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {useLocalClip && savedClip
                ? `Local clip ±${savedClip.padSec}s · ${clock(savedClip.startSec)}–${clock(savedClip.endSec)}`
                : "Video (YouTube)"}
            </Typography>
            {useLocalClip && localClip ? (
              <>
                <Link href={localClip.url} download={localClip.fileName} variant="body2">
                  Save copy
                </Link>
                <Button size="small" onClick={() => setClipHidden(true)}>
                  Use YouTube
                </Button>
              </>
            ) : (
              savedClip &&
              localClip && (
                <Button size="small" onClick={() => setClipHidden(false)}>
                  Use local clip
                </Button>
              )
            )}
          </Stack>
          {useLocalClip && savedClip && localClip ? (
            <>
              <LocalClipPlayer
                url={localClip.url}
                clipStart={savedClip.startSec}
                clipEnd={savedClip.endSec}
                seek={seek}
                initialSec={Math.max(savedClip.startSec, m.segmentStart - CLIP_LEAD_SEC)}
                onTime={setPlayheadSec}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="div"
                sx={{ px: 1, pt: 0.5 }}
                title={savedClip.filePath}
              >
                {savedClip.filePath}
              </Typography>
            </>
          ) : embedUrl ? (
            <Box sx={{ position: "relative", pt: "56.25%" }}>
              <iframe
                key={`${embedUrl}-${seek?.at ?? 0}`}
                src={seekSec == null ? embedUrl : `${embedUrl}&autoplay=1`}
                title="Mention clip"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No YouTube URL for this episode.
            </Typography>
          )}
          {watchUrl && (
            <Box sx={{ px: 1, pt: 1 }}>
              <Link
                href={watchUrl}
                target="_blank"
                rel="noreferrer"
                variant="body2"
              >
                Open on YouTube at {clock(clipStart)}
              </Link>
            </Box>
          )}
        </Paper>

        <CaseTimeline
          detail={data}
          taggedLines={taggedLines}
          playheadSec={useLocalClip ? playheadSec : null}
          onSeek={seekTo}
        />
      </Box>

      {rememberWording.error && (
        <Alert severity="error">
          Could not save the wording. {(rememberWording.error as Error).message}
        </Alert>
      )}
      {clip.error && (
        <Alert severity="error">
          Clip download failed.{" "}
          {(clip.error as { response?: { status?: number } }).response?.status
            ? `(HTTP ${(clip.error as { response?: { status?: number } }).response?.status}) `
            : ""}
          {(clip.error as Error).message}
        </Alert>
      )}
      {replay.error && (
        <Alert severity="error">
          Replay failed. {(replay.error as Error).message}
        </Alert>
      )}
      {data.probe && (
        <ProbePanel
          probe={data.probe}
          segmentStart={m.segmentStart}
          segmentEnd={m.segmentEnd}
          onSeek={seekTo}
        />
      )}
    </Stack>
  );
}

const WHY_LABEL: Record<string, string> = {
  emitted: "Replay emits the brand",
  "not-in-catalog": "Brand not in the analyze catalog",
  blocked: "Brand blocked (inactive or cannot sponsor)",
  "no-evidence": "No evidence in transcript or Loupe",
  "seen-but-dropped": "Evidence present but GPT dropped it",
  "other-brand": "GPT picked another brand",
  "wrong-timing": "Brand emitted, but the span is off",
  "wrong-label": "Brand emitted, but type / placement / cue is off",
  other: "Other",
};

function ProbePanel({
  probe,
  segmentStart,
  segmentEnd,
  onSeek,
}: {
  probe: ReviewAuditCaseProbe;
  segmentStart: number;
  segmentEnd: number;
  onSeek: (sec: number) => void;
}) {
  const theme = useTheme();
  const yesNo = (v: boolean) => (v ? "yes" : "no");
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">Analyze replay</Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(probe.createdAt).toLocaleString()} · window{" "}
            {clock(probe.window.viewStart)}–{clock(probe.window.viewEnd)}
          </Typography>
          {probe.probeId && <CopyId label="replay id" value={probe.probeId} />}
        </Stack>

        <Alert severity={(probe.matchesReview ?? probe.emitted) ? "success" : "warning"}>
          <strong>{WHY_LABEL[probe.why.category] ?? probe.why.category}.</strong>{" "}
          {probe.why.reason}
        </Alert>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            color={probe.emitted ? "success" : "default"}
            label={`emitted: ${yesNo(probe.emitted)}`}
          />
          {probe.review && probe.review.correctedFields.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`corrected: ${probe.review.correctedFields.join(", ")}`}
            />
          )}
          {probe.timing && (
            <Chip
              size="small"
              color={probe.timing.ok ? "success" : "error"}
              label={
                probe.timing.replayStart === null || probe.timing.replayEnd === null
                  ? "replay span: none"
                  : `replay span ${clock(probe.timing.replayStart)}–${clock(probe.timing.replayEnd)} · start Δ${probe.timing.startDeltaSec}s · end Δ${probe.timing.endDeltaSec}s`
              }
            />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={`catalog: ${probe.inputs.brandStatus} (${probe.inputs.catalogSize})`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`spoken: ${yesNo(probe.inputs.brandSpoken)}`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`on screen (Loupe): ${yesNo(probe.inputs.brandOnScreen)}`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={
              probe.run
                ? probe.run.transcriptOnly
                  ? "run: transcript-only"
                  : `run: ${probe.run.algorithmVersion}`
                : "run: none"
            }
          />
        </Stack>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Replay mentions ({probe.mentions.length})
          </Typography>
          {probe.mentions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              The replay emitted nothing in this window.
            </Typography>
          ) : (
            probe.mentions.map((pm) => (
              <Box
                key={`${pm.brand}-${pm.startSec}-${pm.cue}`}
                onClick={() => onSeek(Math.floor(pm.startSec))}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  cursor: "pointer",
                  bgcolor:
                    pm.endSec >= segmentStart && pm.startSec <= segmentEnd
                      ? alpha(theme.palette.warning.main, 0.12)
                      : "transparent",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Typography variant="body2">
                  <strong>{pm.brand}</strong> · {pm.cue}
                  {pm.evidenceType ? ` · ${pm.evidenceType}` : ""} ·{" "}
                  {clock(pm.startSec)}–{clock(pm.endSec)}
                  {pm.confidence != null
                    ? ` · ${Math.round(pm.confidence <= 1 ? pm.confidence * 100 : pm.confidence)}`
                    : ""}
                </Typography>
                {pm.reason && (
                  <Typography variant="caption" color="text.secondary">
                    {pm.reason}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function frameBrands(f: ReviewAuditCaseLoupeFrame): string[] {
  const out = new Set<string>();
  for (const list of Object.values(f.brandsByCue ?? {})) {
    for (const b of list ?? []) if (b) out.add(b);
  }
  for (const b of f.brands) if (b) out.add(b);
  return Array.from(out);
}

type LoupeFrameRow =
  | { kind: "frame"; frame: ReviewAuditCaseLoupeFrame }
  | { kind: "empty"; start: number; end: number; count: number };

/** Consecutive nothing-on-screen seconds collapse into one row. */
function groupLoupeFrames(
  frames: ReviewAuditCaseLoupeFrame[],
): LoupeFrameRow[] {
  const rows: LoupeFrameRow[] = [];
  for (const frame of frames) {
    const empty = !frame.present && frameBrands(frame).length === 0;
    const last = rows[rows.length - 1];
    if (empty && last?.kind === "empty") {
      last.end = frame.tsec;
      last.count += 1;
    } else if (empty) {
      rows.push({
        kind: "empty",
        start: frame.tsec,
        end: frame.tsec,
        count: 1,
      });
    } else {
      rows.push({ kind: "frame", frame });
    }
  }
  return rows;
}

function scoreText(label: string, v: number | null): string | null {
  if (v == null) return null;
  return `${label} ${Math.round(v <= 1 ? v * 100 : v)}`;
}

function loupeRowStart(row: LoupeFrameRow): number {
  return row.kind === "empty" ? row.start : row.frame.tsec;
}

type TimelineSlot = {
  start: number;
  end: number;
  lines: { line: ReviewAuditCaseDetail["transcript"][number]; index: number }[];
  loupe: LoupeFrameRow[];
};

/**
 * One slot per transcript line (until the next line starts); Loupe rows land in
 * the slot their first second falls in, so both columns share one scroll.
 */
function buildTimelineSlots(
  transcript: ReviewAuditCaseDetail["transcript"],
  loupeRows: LoupeFrameRow[],
): TimelineSlot[] {
  const slots: TimelineSlot[] = transcript.map((line, index) => ({
    start: line.start,
    end: transcript[index + 1]?.start ?? line.end,
    lines: [{ line, index }],
    loupe: [],
  }));
  for (const row of loupeRows) {
    const at = loupeRowStart(row);
    let slot: TimelineSlot | undefined;
    for (let i = slots.length - 1; i >= 0; i -= 1) {
      if (slots[i].start <= at) {
        slot = slots[i];
        break;
      }
    }
    if (!slot) {
      const first = slots[0];
      if (first && first.lines.length === 0) {
        slot = first;
      } else {
        slot = { start: at, end: first?.start ?? at, lines: [], loupe: [] };
        slots.unshift(slot);
      }
    }
    slot.loupe.push(row);
    slot.end = Math.max(slot.end, row.kind === "empty" ? row.end : row.frame.tsec);
  }
  return slots;
}

/**
 * One slot per second of the window: the words spoken in that second (the whole
 * line on its first second when the transcript has no word timings) beside that
 * second's Loupe frame, so frames can be checked one by one.
 */
function buildSecondSlots(
  transcript: ReviewAuditCaseDetail["transcript"],
  frames: ReviewAuditCaseLoupeFrame[],
  window: { start: number; end: number },
): TimelineSlot[] {
  const from = Math.floor(
    Math.min(window.start, transcript[0]?.start ?? Infinity, frames[0]?.tsec ?? Infinity),
  );
  const to = Math.floor(
    Math.max(
      window.end,
      transcript[transcript.length - 1]?.end ?? -Infinity,
      frames[frames.length - 1]?.tsec ?? -Infinity,
    ),
  );
  if (!Number.isFinite(from) || !Number.isFinite(to)) return [];
  const slots: TimelineSlot[] = [];
  for (let s = from; s <= to; s += 1) {
    slots.push({ start: s, end: s + 1, lines: [], loupe: [] });
  }
  const at = (sec: number) => slots[Math.min(slots.length - 1, Math.max(0, Math.floor(sec) - from))];

  const spoken = new Map<TimelineSlot, Map<number, string[]>>();
  const say = (slot: TimelineSlot, index: number, text: string) => {
    const byLine = spoken.get(slot) ?? new Map<number, string[]>();
    byLine.set(index, [...(byLine.get(index) ?? []), text]);
    spoken.set(slot, byLine);
  };
  transcript.forEach((line, index) => {
    if (line.words?.length) {
      for (const w of line.words) say(at(w.start), index, w.word);
    } else {
      say(at(line.start), index, line.text);
    }
  });
  spoken.forEach((byLine, slot) => {
    byLine.forEach((parts, index) => {
      const line = transcript[index];
      const text = line.words?.length ? parts.join("").trim() : parts.join(" ");
      if (text) slot.lines.push({ line: { start: slot.start, end: slot.end, text }, index });
    });
  });

  for (const frame of frames) {
    const empty = !frame.present && frameBrands(frame).length === 0;
    at(frame.tsec).loupe.push(
      empty
        ? { kind: "empty", start: frame.tsec, end: frame.tsec, count: 1 }
        : { kind: "frame", frame },
    );
  }
  return slots;
}

const PER_SECOND_STORAGE_KEY = "reviewAuditCases.timelinePerSecond";

function LoupeTimelineRow({
  row,
  onSeek,
}: {
  row: LoupeFrameRow;
  onSeek: (sec: number) => void;
}) {
  if (row.kind === "empty") {
    return (
      <Box
        sx={{ cursor: "pointer", py: 0.25 }}
        onClick={(e) => {
          e.stopPropagation();
          onSeek(Math.floor(row.start));
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {clock(row.start)}
          {row.end > row.start ? `–${clock(row.end)}` : ""} · nothing on screen
          {row.count > 1 ? ` (${row.count} frames)` : ""}
        </Typography>
      </Box>
    );
  }
  const f = row.frame;
  const cue = f.effectiveCue ?? f.cue ?? "none";
  const brands = frameBrands(f);
  const scores = [
    scoreText("sieve", f.sieveConfidence),
    scoreText("cue", f.cueConfidence),
    scoreText("seg", f.segmentConfidence),
  ].filter(Boolean);
  const reason = f.segmentReason ?? f.cueReason ?? f.sieveReason;
  return (
    <Box
      sx={{ cursor: "pointer", py: 0.25 }}
      onClick={(e) => {
        e.stopPropagation();
        onSeek(Math.floor(f.tsec));
      }}
      title={[
        f.sieveReason && `sieve: ${f.sieveReason}`,
        f.cueReason && `cue: ${f.cueReason}`,
        f.segmentReason && `segment: ${f.segmentReason}`,
      ]
        .filter(Boolean)
        .join("\n")}
    >
      <Typography variant="body2">
        <Box
          component="span"
          sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums", mr: 1 }}
        >
          {clock(f.tsec)}
        </Box>
        <strong>{cue}</strong>
        {f.cueSet.length > 1 ? ` (${f.cueSet.join("+")})` : ""}
        {brands.length > 0 ? ` · ${brands.join(", ")}` : ""}
        {f.cueDecision === "review" ? " · review" : ""}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {f.sieveCue && f.sieveCue !== cue ? `sieve said ${f.sieveCue} · ` : ""}
        {scores.join(" · ")}
      </Typography>
      {reason && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {reason}
        </Typography>
      )}
    </Box>
  );
}

const LOUPE_UI_DATASET_URL = "http://127.0.0.1:8790/?tab=dataset";

function datasetFrameLabel(f: ReviewAuditDatasetFrame): string {
  if (!f.present || f.cues.length === 0) return `${clock(f.tsec)} absent`;
  const cues = f.cues
    .map((c) => (c.brands?.length ? `${c.cueClass} · ${c.brands.join(", ")}` : c.cueClass))
    .join(" + ");
  return `${clock(f.tsec)} ${cues}`;
}

type SaveFrameTarget = {
  anchor: HTMLElement;
  tsec: number;
  frames: ReviewAuditCaseLoupeFrame[];
};

/** Loupe's guess at `tsec` (nearest frame at or before it), used to prefill the form. */
function loupeGuessAt(frames: ReviewAuditCaseLoupeFrame[], tsec: number) {
  const before = frames.filter((f) => f.tsec <= tsec);
  const f = before[before.length - 1] ?? frames[0];
  if (!f) return { cue: "absent", brands: "" };
  const cue = f.effectiveCue ?? f.cue ?? "";
  return {
    cue: (LOUPE_CUE_CLASSES as readonly string[]).includes(cue) ? cue : "absent",
    brands: frameBrands(f).join(", "),
  };
}

function SaveDatasetFrameForm({
  mentionId,
  target,
  onSaved,
}: {
  mentionId: string;
  target: SaveFrameTarget;
  onSaved: () => void;
}) {
  const guess = loupeGuessAt(target.frames, target.tsec);
  const [tsec, setTsec] = useState(String(target.tsec));
  const [cue, setCue] = useState(guess.cue);
  const [brands, setBrands] = useState(guess.brands);
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () =>
      apiService.saveReviewAuditDatasetFrame(mentionId, {
        tsec: Math.round(Number(tsec)),
        cue,
        brands: brands
          .split(",")
          .map((b) => b.trim())
          .filter(Boolean),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: onSaved,
  });
  const secValid = Number.isFinite(Number(tsec)) && Number(tsec) >= 0 && tsec.trim() !== "";
  return (
    <Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
      <Typography variant="subtitle2">Save frame to Loupe dataset</Typography>
      <TextField
        size="small"
        label="Second"
        type="number"
        value={tsec}
        onChange={(e) => setTsec(e.target.value)}
        helperText={secValid ? clock(Number(tsec)) : "Enter a second"}
        error={!secValid}
      />
      <TextField
        size="small"
        select
        label="Cue"
        value={cue}
        onChange={(e) => setCue(e.target.value)}
      >
        <MenuItem value="absent">absent (nothing sponsor-shaped)</MenuItem>
        {LOUPE_CUE_CLASSES.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Brands (comma separated)"
        value={brands}
        disabled={cue === "absent"}
        onChange={(e) => setBrands(e.target.value)}
      />
      <TextField
        size="small"
        label="Note"
        placeholder={`review-audit ${mentionId}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {save.error && (
        <Alert severity="error">{(save.error as Error).message}</Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          size="small"
          disabled={!secValid || save.isPending}
          onClick={() => save.mutate()}
          startIcon={save.isPending ? <CircularProgress size={14} /> : undefined}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Link href={LOUPE_UI_DATASET_URL} target="_blank" rel="noreferrer" variant="body2">
          Open dataset
        </Link>
      </Stack>
    </Stack>
  );
}

/** Transcript and Loupe side by side, one row per time slot, one scroll. */
function CaseTimeline({
  detail,
  taggedLines,
  playheadSec,
  onSeek,
}: {
  detail: ReviewAuditCaseDetail;
  taggedLines: Set<number>;
  playheadSec: number | null;
  onSeek: (sec: number) => void;
}) {
  const theme = useTheme();
  const m = detail.mention;
  const loupe = detail.loupe;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [perSecond, setPerSecond] = useState(
    () => localStorage.getItem(PER_SECOND_STORAGE_KEY) === "1",
  );
  const togglePerSecond = () =>
    setPerSecond((on) => {
      localStorage.setItem(PER_SECOND_STORAGE_KEY, on ? "0" : "1");
      return !on;
    });
  const slots = useMemo(() => {
    const frames = loupe.status === "visual" ? loupe.frames : [];
    return perSecond
      ? buildSecondSlots(detail.transcript, frames, detail.transcriptWindow)
      : buildTimelineSlots(detail.transcript, groupLoupeFrames(frames));
  }, [detail.transcript, detail.transcriptWindow, loupe, perSecond]);

  useEffect(() => {
    const box = scrollRef.current;
    const first = box?.querySelector<HTMLElement>('[data-inside="true"]');
    if (box && first) box.scrollTop = Math.max(0, first.offsetTop - 48);
  }, [m.mentionId, slots]);

  const queryClient = useQueryClient();
  const rangeFrom = Math.floor(Math.min(detail.transcriptWindow.start, slots[0]?.start ?? Infinity));
  const rangeTo = Math.ceil(
    Math.max(detail.transcriptWindow.end, slots[slots.length - 1]?.end ?? -Infinity),
  );
  const datasetKey = ["reviewAuditDatasetFrames", m.mentionId, rangeFrom, rangeTo];
  const { data: datasetFrames = [] } = useQuery({
    queryKey: datasetKey,
    queryFn: () => apiService.getReviewAuditDatasetFrames(m.mentionId, rangeFrom, rangeTo),
    enabled: Number.isFinite(rangeFrom) && Number.isFinite(rangeTo),
    staleTime: 60_000,
  });
  const [saveTarget, setSaveTarget] = useState<SaveFrameTarget | null>(null);
  useEffect(() => setSaveTarget(null), [m.mentionId]);

  const openSaveFrame = (anchor: HTMLElement, slot: TimelineSlot) => {
    const slotFrames = slot.loupe.flatMap((r) => (r.kind === "frame" ? [r.frame] : []));
    const playheadInSlot =
      playheadSec != null && playheadSec >= slot.start && playheadSec < slot.end;
    const tsec = playheadInSlot
      ? Math.round(playheadSec)
      : Math.round(slotFrames[0]?.tsec ?? slot.start);
    setSaveTarget({ anchor, tsec, frames: loupe.frames });
  };

  const playingIndex =
    playheadSec == null
      ? -1
      : slots.findIndex((s) => playheadSec >= s.start && playheadSec < s.end);

  useEffect(() => {
    if (playingIndex < 0) return;
    const box = scrollRef.current;
    const row = box?.querySelector<HTMLElement>(`[data-slot="${playingIndex}"]`);
    if (!box || !row) return;
    const top = row.offsetTop;
    if (top < box.scrollTop + 40 || top > box.scrollTop + box.clientHeight - 60) {
      box.scrollTo({ top: Math.max(0, top - 48), behavior: "smooth" });
    }
  }, [playingIndex]);

  const loupeNote =
    loupe.status === "visual"
      ? `${loupe.algorithmVersion} · ${loupe.frames.length} frames`
      : loupe.status === "transcript-only"
        ? "transcript-only run — no visual output"
        : "not run on this episode";

  return (
    <Paper
      variant="outlined"
      ref={scrollRef}
      sx={{ maxHeight: COLUMN_MAX_HEIGHT, overflow: "auto", position: "relative" }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderRight: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2">
            Transcript {clock(detail.transcriptWindow.start)}–
            {clock(detail.transcriptWindow.end)}
          </Typography>
          {!detail.transcriptAvailable && (
            <Typography variant="caption" color="text.secondary">
              This episode has no transcript file.
            </Typography>
          )}
        </Box>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Loupe</Typography>
            <Tooltip
              title={
                perSecond
                  ? "Back to one row per transcript line"
                  : "One row per second: that second's words beside that second's frame"
              }
            >
              <Button size="small" onClick={togglePerSecond} sx={{ minWidth: 0, py: 0 }}>
                {perSecond ? "Per line" : "Per second"}
              </Button>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {loupeNote}
          </Typography>
          {loupe.status === "visual" && loupe.brandGroups.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
              {loupe.brandGroups.map((g) => (
                <Chip
                  key={`${g.brand}-${g.cue}-${g.startSec}`}
                  size="small"
                  variant="outlined"
                  label={`${g.brand || "(no brand)"} · ${g.cue} · ${clock(g.startSec)}–${clock(g.endSec)}`}
                  title={[
                    g.cueConfidence != null && `cue ${Math.round(g.cueConfidence)}`,
                    g.brandConfidence != null && `brand ${Math.round(g.brandConfidence)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  onClick={() => onSeek(Math.floor(g.startSec))}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {slots.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No speech or frames in this window.
        </Typography>
      ) : (
        slots.map((slot, i) => {
          const inside = slot.end > m.segmentStart && slot.start < m.segmentEnd;
          const playing = i === playingIndex;
          const isLast = i === slots.length - 1;
          const slotSaved = datasetFrames.filter(
            (f) => f.tsec >= Math.floor(slot.start) && (isLast ? f.tsec <= slot.end : f.tsec < slot.end),
          );
          return (
            <Box
              key={`${slot.start}-${i}`}
              data-slot={i}
              data-inside={inside ? "true" : undefined}
              onClick={() => onSeek(Math.floor(slot.start))}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                cursor: "pointer",
                borderBottom: 1,
                borderColor: "divider",
                borderLeft: 3,
                borderLeftColor: playing ? theme.palette.primary.main : "transparent",
                bgcolor: inside ? alpha(theme.palette.warning.main, 0.12) : "transparent",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                "&:hover .save-frame": { opacity: 1 },
              }}
            >
              <Box
                sx={{ px: 1.5, py: perSecond ? 0.25 : 0.75, borderRight: 1, borderColor: "divider" }}
              >
                {slot.lines.map(({ line, index }) => (
                  <Stack key={`${index}-${line.start}`} direction="row" spacing={1.5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 48, fontVariantNumeric: "tabular-nums", pt: 0.25 }}
                    >
                      {clock(line.start)}
                    </Typography>
                    <Typography variant="body2">
                      {taggedLines.has(index) && (
                        <Chip
                          component="span"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          label={`${m.brandName} sponsor read`}
                          title="Matches a saved read; analyze sees this line tagged as the brand"
                          sx={{ mr: 0.75, height: 20, verticalAlign: "text-bottom" }}
                        />
                      )}
                      {line.text}
                    </Typography>
                  </Stack>
                ))}
              </Box>
              <Box sx={{ px: 1.5, py: perSecond ? 0.25 : 0.75, position: "relative", pr: 5 }}>
                <Tooltip title="Save a frame from this slot to the Loupe dataset">
                  <IconButton
                    size="small"
                    className="save-frame"
                    onClick={(e) => {
                      e.stopPropagation();
                      openSaveFrame(e.currentTarget, slot);
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 4,
                      opacity: slotSaved.length > 0 ? 0.8 : 0.25,
                    }}
                  >
                    <AddPhotoAlternateOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {slot.loupe.map((row) => (
                  <LoupeTimelineRow
                    key={`${row.kind}-${loupeRowStart(row)}`}
                    row={row}
                    onSeek={onSeek}
                  />
                ))}
                {perSecond && slot.loupe.length === 0 && loupe.status === "visual" && (
                  <Typography variant="caption" color="text.disabled">
                    {clock(slot.start)} · no frame
                  </Typography>
                )}
                {slotSaved.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                    {slotSaved.map((f) => (
                      <Chip
                        key={f.frameId}
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`dataset ${datasetFrameLabel(f)}`}
                        title={[f.frameId, f.note, f.labeledAt].filter(Boolean).join("\n")}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSeek(f.tsec);
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          );
        })
      )}
      <Popover
        open={Boolean(saveTarget)}
        anchorEl={saveTarget?.anchor}
        onClose={() => setSaveTarget(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {saveTarget && (
          <SaveDatasetFrameForm
            key={`${saveTarget.tsec}-${m.mentionId}`}
            mentionId={m.mentionId}
            target={saveTarget}
            onSaved={() => {
              setSaveTarget(null);
              void queryClient.invalidateQueries({ queryKey: datasetKey });
            }}
          />
        )}
      </Popover>
    </Paper>
  );
}

function LocalClipPlayer({
  url,
  clipStart,
  clipEnd,
  seek,
  initialSec,
  onTime,
}: {
  url: string;
  clipStart: number;
  clipEnd: number;
  seek: { sec: number; at: number } | null;
  initialSec: number;
  onTime: (episodeSec: number) => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const target = seek?.sec ?? initialSec;
  const outside = target < clipStart || target > clipEnd;

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    const apply = () => {
      video.currentTime = Math.min(Math.max(0, target - clipStart), clipEnd - clipStart);
      if (seek) void video.play().catch(() => undefined);
    };
    if (video.readyState >= 1) {
      apply();
      return undefined;
    }
    video.addEventListener("loadedmetadata", apply, { once: true });
    return () => video.removeEventListener("loadedmetadata", apply);
  }, [url, target, seek, clipStart, clipEnd]);

  return (
    <Box>
      <video
        ref={ref}
        src={url}
        controls
        onTimeUpdate={(e) => onTime(clipStart + e.currentTarget.currentTime)}
        style={{ width: "100%", maxHeight: 420, background: "#000", display: "block" }}
      />
      {outside && seek && (
        <Typography variant="caption" color="warning.main" sx={{ px: 1 }}>
          {clock(target)} is outside the local clip ({clock(clipStart)}–{clock(clipEnd)}).
        </Typography>
      )}
    </Box>
  );
}

function mentionSecondary(m: ReviewAuditCaseMention): string {
  const detail =
    m.kind === "rejected"
      ? m.rejectReason || "(no reason)"
      : m.kind === "edited"
        ? m.editedFields.join(", ")
        : [m.placementType, m.evidenceType].filter(Boolean).join(" · ") ||
          "added";
  return `${clock(m.segmentStart)} · ${m.podcastTitle} · ${detail}`;
}

const DATES_STORAGE_KEY = "reviewAuditCases.dates";
const FILTERS_STORAGE_KEY = "reviewAuditCases.filters";
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const ALL_MODE = "__all__";
const BATCH_CONCURRENCY = 3;
const BATCH_MAX = 50;

function loadStoredDates(): { dateFrom: string; dateTo: string } {
  const fallback = {
    dateFrom: ymdDaysAgo(DEFAULT_LOOKBACK_DAYS),
    dateTo: ymdDaysAgo(0),
  };
  try {
    const raw = localStorage.getItem(DATES_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as { dateFrom?: unknown; dateTo?: unknown };
    if (
      typeof parsed.dateFrom === "string" &&
      typeof parsed.dateTo === "string" &&
      YMD.test(parsed.dateFrom) &&
      YMD.test(parsed.dateTo)
    ) {
      return { dateFrom: parsed.dateFrom, dateTo: parsed.dateTo };
    }
  } catch {
    // ignore unreadable storage
  }
  return fallback;
}

type CaseFilters = {
  kind: ReviewAuditCaseKind | "";
  placement: string;
  evidence: string;
  podcastId: string;
};

const EMPTY_FILTERS: CaseFilters = {
  kind: "",
  placement: "",
  evidence: "",
  podcastId: "",
};

function loadStoredFilters(): CaseFilters {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_FILTERS;
    const parsed = JSON.parse(raw) as Partial<Record<keyof CaseFilters, unknown>>;
    const str = (v: unknown) => (typeof v === "string" ? v : "");
    const kind = str(parsed.kind);
    return {
      kind: kind in KIND_LABEL ? (kind as ReviewAuditCaseKind) : "",
      placement: str(parsed.placement),
      evidence: str(parsed.evidence),
      podcastId: str(parsed.podcastId),
    };
  } catch {
    return EMPTY_FILTERS;
  }
}

function passesFilters(m: ReviewAuditCaseMention, f: CaseFilters): boolean {
  if (f.kind && m.kind !== f.kind) return false;
  if (f.placement && m.placementType !== f.placement) return false;
  if (f.evidence && m.evidenceType !== f.evidence) return false;
  if (f.podcastId && m.podcastId !== f.podcastId) return false;
  return true;
}

function distinct(values: Array<string | null>): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v)))).sort();
}

type RowRun =
  | { status: "queued" }
  | { status: "running" }
  | { status: "done"; probe: ReviewAuditCaseLastProbe }
  | { status: "error"; error: string };

function RowStatus({
  run,
  lastProbe,
}: {
  run: RowRun | undefined;
  lastProbe: ReviewAuditCaseLastProbe | null;
}) {
  const box = (node: React.ReactNode, title: string) => (
    <Tooltip title={title}>
      <Box
        sx={{
          width: 20,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {node}
      </Box>
    </Tooltip>
  );
  if (run?.status === "queued") {
    return box(
      <FiberManualRecordIcon sx={{ fontSize: 8, color: "text.disabled" }} />,
      "Queued",
    );
  }
  if (run?.status === "running") {
    return box(<CircularProgress size={14} />, "Replaying analyze…");
  }
  if (run?.status === "error") {
    return box(
      <CancelIcon sx={{ fontSize: 18 }} color="error" />,
      `Replay failed: ${run.error}`,
    );
  }
  const probe = run?.status === "done" ? run.probe : lastProbe;
  if (!probe) return box(null, "Never replayed");
  const when = probe.createdAt
    ? new Date(probe.createdAt).toLocaleString()
    : "unknown time";
  const why = WHY_LABEL[probe.whyCategory] ?? probe.whyCategory;
  return probe.matchesReview
    ? box(
        <CheckCircleIcon sx={{ fontSize: 18 }} color="success" />,
        `Replay matches review · ${why} · ${when}`,
      )
    : box(
        <CancelIcon sx={{ fontSize: 18 }} color="error" />,
        `Replay still wrong · ${why} · ${when}`,
      );
}

export default function ReviewAuditCases() {
  const queryClient = useQueryClient();
  const [initialDates] = useState(loadStoredDates);
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [applied, setApplied] = useState(initialDates);
  const [filters, setFilters] = useState<CaseFilters>(loadStoredFilters);

  useEffect(() => {
    try {
      localStorage.setItem(DATES_STORAGE_KEY, JSON.stringify(applied));
    } catch {
      // ignore full or disabled storage
    }
  }, [applied]);
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // ignore full or disabled storage
    }
  }, [filters]);

  const [mode, setMode] = useState<string>(ALL_MODE);
  const [mentionId, setMentionId] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, RowRun>>({});
  const clearRun = useCallback((id: string) => {
    setRuns((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const stopRef = useRef(false);

  useEffect(
    () => () => {
      stopRef.current = true;
    },
    [],
  );

  const { data, isFetching, error } = useQuery<ReviewAuditCasesSummary>({
    queryKey: ["reviewAuditCases", applied],
    queryFn: () => apiService.getReviewAuditCases(applied),
    staleTime: 60_000,
  });

  const categories = useMemo(() => data?.categories ?? [], [data]);

  const options = useMemo(() => {
    const rows = categories.flatMap((c) => c.mentions);
    const podcasts = new Map<string, string>();
    for (const m of rows) podcasts.set(m.podcastId, m.podcastTitle);
    return {
      placements: distinct(rows.map((m) => m.placementType)),
      evidence: distinct(rows.map((m) => m.evidenceType)),
      podcasts: Array.from(podcasts, ([id, title]) => ({ id, title })).sort(
        (a, b) => a.title.localeCompare(b.title),
      ),
    };
  }, [categories]);

  const filtered = useMemo(() => {
    const cats = categories
      .map((c) => {
        const mentions = c.mentions.filter((m) => passesFilters(m, filters));
        return { ...c, mentions, count: mentions.length };
      })
      .filter((c) => c.count > 0);
    const seen = new Map<string, ReviewAuditCaseMention>();
    for (const c of cats) for (const m of c.mentions) seen.set(m.mentionId, m);
    const all = Array.from(seen.values()).sort((a, b) =>
      (b.reviewedAt ?? "").localeCompare(a.reviewedAt ?? ""),
    );
    return { categories: cats, all };
  }, [categories, filters]);

  const selected = useMemo(() => {
    const allEntry = {
      mode: ALL_MODE,
      label: "All categories",
      mentions: filtered.all,
      count: filtered.all.length,
    };
    if (mode === ALL_MODE) return allEntry;
    return filtered.categories.find((c) => c.mode === mode) ?? allEntry;
  }, [filtered, mode]);

  const listed = selected.mentions;
  const selectedPodcast =
    options.podcasts.find((p) => p.id === filters.podcastId) ?? null;
  const filtersActive = Object.values(filters).some(Boolean);

  const setFilter = <K extends keyof CaseFilters>(key: K, value: CaseFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setMentionId(null);
  };

  const runBatch = useCallback(
    async (rows: ReviewAuditCaseMention[]) => {
      const batch = rows.slice(0, BATCH_MAX);
      if (batch.length === 0) return;
      stopRef.current = false;
      setBatchRunning(true);
      setBatchIds(batch.map((m) => m.mentionId));
      setRuns((prev) => {
        const next = { ...prev };
        for (const m of batch) next[m.mentionId] = { status: "queued" };
        return next;
      });

      let cursor = 0;
      const worker = async () => {
        while (!stopRef.current && cursor < batch.length) {
          const m = batch[cursor++]!;
          setRuns((prev) => ({ ...prev, [m.mentionId]: { status: "running" } }));
          try {
            const probe = await apiService.runReviewAuditCaseProbe(m.mentionId);
            queryClient.setQueryData<ReviewAuditCaseDetail>(
              ["reviewAuditCase", m.mentionId],
              (prev) => (prev ? { ...prev, probe } : prev),
            );
            const matchesReview =
              probe.matchesReview ?? (m.kind === "rejected" ? !probe.emitted : probe.emitted);
            setRuns((prev) => ({
              ...prev,
              [m.mentionId]: {
                status: "done",
                probe: {
                  probeId: probe.probeId ?? "",
                  emitted: probe.emitted,
                  matchesReview,
                  whyCategory: probe.why.category,
                  createdAt: probe.createdAt,
                },
              },
            }));
          } catch (err) {
            const message =
              (err as { response?: { data?: { message?: string } } }).response?.data
                ?.message ?? (err as Error).message;
            setRuns((prev) => ({
              ...prev,
              [m.mentionId]: { status: "error", error: message },
            }));
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(BATCH_CONCURRENCY, batch.length) }, worker),
      );

      setRuns((prev) => {
        const next = { ...prev };
        for (const m of batch) {
          if (next[m.mentionId]?.status === "queued") delete next[m.mentionId];
        }
        return next;
      });
      setBatchRunning(false);
      void queryClient.invalidateQueries({ queryKey: ["reviewAuditCases"] });
    },
    [queryClient],
  );

  const progress = useMemo(() => {
    let done = 0;
    let match = 0;
    let wrong = 0;
    let failed = 0;
    for (const id of batchIds) {
      const run = runs[id];
      if (run?.status === "done") {
        done += 1;
        if (run.probe.matchesReview) match += 1;
        else wrong += 1;
      } else if (run?.status === "error") {
        done += 1;
        failed += 1;
      }
    }
    return { done, match, wrong, failed, total: batchIds.length };
  }, [batchIds, runs]);

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">Review audit cases</Typography>
        <Link
          component={RouterLink}
          to={ROUTES_NAMES.ADMIN_REVIEW_AUDIT}
          variant="body2"
        >
          Back to review audit
        </Link>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          label="Reviewed from"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Reviewed to"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          disabled={batchRunning}
          onClick={() => {
            setApplied({ dateFrom, dateTo });
            setMode(ALL_MODE);
            setMentionId(null);
            setRuns({});
            setBatchIds([]);
          }}
        >
          Load
        </Button>
        {isFetching && <CircularProgress size={20} />}
        {data && (
          <Typography variant="body2" color="text.secondary">
            {data.totalWrong} wrong paid mentions
          </Typography>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <TextField
          select
          label="Kind"
          size="small"
          value={filters.kind}
          onChange={(e) =>
            setFilter("kind", e.target.value as CaseFilters["kind"])
          }
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All kinds</MenuItem>
          {(Object.keys(KIND_LABEL) as ReviewAuditCaseKind[]).map((k) => (
            <MenuItem key={k} value={k}>
              {KIND_LABEL[k]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Placement"
          size="small"
          value={filters.placement}
          onChange={(e) => setFilter("placement", e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All placements</MenuItem>
          {options.placements.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Evidence"
          size="small"
          value={filters.evidence}
          onChange={(e) => setFilter("evidence", e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All evidence</MenuItem>
          {options.evidence.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          size="small"
          options={options.podcasts}
          value={selectedPodcast}
          onChange={(_, value) => setFilter("podcastId", value?.id ?? "")}
          getOptionLabel={(o) => o.title}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          sx={{ width: 280 }}
          renderInput={(params) => <TextField {...params} label="Podcast" />}
        />
        {filtersActive && (
          <Button
            size="small"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setMentionId(null);
            }}
          >
            Clear filters
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load cases. {(error as Error).message}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "230px 340px minmax(0, 1fr)",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper variant="outlined">
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
            Categories
          </Typography>
          <List dense>
            {categories.length > 0 && (
              <ListItemButton
                selected={selected.mode === ALL_MODE}
                onClick={() => {
                  setMode(ALL_MODE);
                  setMentionId(null);
                }}
              >
                <ListItemText primary="All categories" />
                <Chip size="small" label={filtered.all.length} />
              </ListItemButton>
            )}
            {filtered.categories.map((c) => (
              <ListItemButton
                key={c.mode}
                selected={selected.mode === c.mode}
                onClick={() => {
                  setMode(c.mode);
                  setMentionId(null);
                }}
              >
                <ListItemText
                  primary={c.label}
                  secondary={
                    c.kind === "rejected"
                      ? "Reject"
                      : c.kind === "edited"
                        ? "Edit"
                        : "Miss"
                  }
                />
                <Chip size="small" label={c.count} />
              </ListItemButton>
            ))}
            {!isFetching && filtered.all.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, py: 1 }}
              >
                {categories.length === 0
                  ? "No wrong paid mentions in this window."
                  : "No mentions match these filters."}
              </Typography>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ maxHeight: "80vh", overflow: "auto" }}>
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography variant="subtitle2">
              {selected.label} ({selected.count})
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
              {batchRunning ? (
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={() => {
                    stopRef.current = true;
                  }}
                >
                  Stop
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={listed.length === 0}
                  onClick={() => void runBatch(listed)}
                >
                  Run analysis on {Math.min(listed.length, BATCH_MAX)} listed
                </Button>
              )}
              {progress.total > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {progress.done}/{progress.total} · {progress.match} match ·{" "}
                  {progress.wrong} wrong
                  {progress.failed ? ` · ${progress.failed} failed` : ""}
                </Typography>
              )}
            </Stack>
            {listed.length > BATCH_MAX && !batchRunning && (
              <Typography variant="caption" color="text.secondary">
                Runs the first {BATCH_MAX}; narrow the filters for the rest.
              </Typography>
            )}
          </Box>
          <List dense>
            {listed.map((m) => (
              <ListItemButton
                key={m.mentionId}
                selected={mentionId === m.mentionId}
                onClick={() => setMentionId(m.mentionId)}
                sx={{ gap: 1 }}
              >
                <RowStatus run={runs[m.mentionId]} lastProbe={m.lastProbe} />
                <ListItemText
                  primary={m.brandName}
                  secondary={mentionSecondary(m)}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Box>
          {mentionId ? (
            <CasePanel mentionId={mentionId} onReplayed={clearRun} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Pick a mention to see its clip and transcript.
            </Typography>
          )}
        </Box>
      </Box>
    </Container>
  );
}
